/**
 * ActivitiesDataTable - Activities table using the reusable DataTable component
 * Pixel-perfect implementation matching Figma design specs
 */

// react
import React, { useCallback, useContext, useMemo } from 'react';

// router
import { useNavigate } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { alpha, Avatar, Box, Stack, styled, Typography } from '@mui/material';

// icons
import {
	OpenInNew as IconExternalLink,
	AccountTree as IconActivity,
	DeleteForever as IconDelete,
	Edit as IconEdit,
	Add as IconAdd,
	Link as IconLink,
} from '@mui/icons-material';

// context
import { ContextProject } from '../../Project/Project.context';
import { ContextSpendPlan } from '../ProjectV2.context';

// components
import { DataTable, ColumnDef, RowAction, FilterConfig } from '../../../ui/DataTable/DataTable';
import { TruncatedText } from '../../../ui/TruncatedText/TruncatedText';

// data
import { GRADIENTS } from '../../../../data';

// types
import { PlanningActivity } from '../../../../@types';

// utils
import { formatCurrency } from '../../../../utils/currency';
import { formatDate } from '../../../../utils/dates';

// Styled Components - Pixel-perfect Figma match

// Activity name link - blue, underlined, clickable
const ActivityLink = styled(Typography)({
	fontSize: 14,
	fontWeight: 600, // Bold to match mockup
	lineHeight: 1.4,
	color: '#2563eb',
	cursor: 'pointer',
	textDecoration: 'underline',
	textDecorationColor: '#2563eb',
	textUnderlineOffset: 2,
	'&:hover': {
		color: '#1d4ed8',
	},
});

// Component text style - purple
const ComponentText = styled(Typography)({
	fontSize: 14,
	fontWeight: 400,
	lineHeight: 1.4,
	color: '#7c3aed', // Purple
});

// Category text style - orange/brown
const CategoryText = styled(Typography)({
	fontSize: 14,
	fontWeight: 400,
	lineHeight: 1.4,
	color: '#b45309', // Orange/brown matching mockup
});

// Progress chip with arrow
const ProgressChip = styled(Box)<{ value: number }>(({ value }) => ({
	display: 'inline-flex',
	alignItems: 'center',
	gap: 4,
	fontSize: 13,
	fontWeight: 500,
	color: value > 75 ? '#15803d' : value > 25 ? '#ca8a04' : '#6b7280',
	padding: '4px 8px',
	backgroundColor: value > 75 ? '#f0fdf4' : value > 25 ? '#fefce8' : '#f9fafb',
	borderRadius: 4,
	border: `1px solid ${value > 75 ? '#86efac' : value > 25 ? '#fde047' : '#e5e7eb'}`,
}));

// Work plan link
const WorkPlanLink = styled(Typography)({
	fontSize: 12,
	fontWeight: 400,
	lineHeight: 1.3,
	color: '#6b7280',
	display: 'flex',
	alignItems: 'center',
	gap: 4,
	cursor: 'pointer',
	'&:hover': {
		color: '#374151',
	},
});

// Types

interface ActivitiesDataTableProps {
	isLoading?: boolean;
	onAddActivity?: () => void;
	onAddSubActivity: (activity: PlanningActivity) => void;
	onDeactivateActivity: (activity: PlanningActivity) => void;
	onDeleteDraftActivity?: (activity: PlanningActivity) => void;
	onEditActivity: (activity: PlanningActivity) => void;
	onLinkContract: (activity: PlanningActivity) => void;
	onSortChange: (orderBy: string, order: 'ASC' | 'DESC') => void;
	order: 'ASC' | 'DESC';
	orderBy: string;
	page: number;
	rowsPerPage: number;
	totalCount: number;
	onPageChange: (page: number) => void;
	onRowsPerPageChange: (rowsPerPage: number) => void;
	searchValue?: string;
	onSearchChange?: (value: string) => void;
	filterValues?: Record<string, string | number>;
	onFilterChange?: (key: string, value: string | number) => void;
}

// Component

export const ActivitiesDataTable = ({
	isLoading = false,
	onAddActivity,
	onAddSubActivity,
	onDeactivateActivity,
	onDeleteDraftActivity,
	onEditActivity,
	onLinkContract,
	onSortChange,
	order,
	orderBy,
	page,
	rowsPerPage,
	totalCount,
	onPageChange,
	onRowsPerPageChange,
	searchValue = '',
	onSearchChange,
	filterValues = {},
	onFilterChange,
}: ActivitiesDataTableProps) => {
	// Hooks
	const { t } = useTranslation();
	const navigate = useNavigate();

	// Context
	const { project } = useContext(ContextProject);
	const { activities: allActivities, workPlans } = useContext(ContextSpendPlan);

	// Client-side pagination: slice the full dataset for the current page
	const activities = useMemo(() => {
		const start = (page - 1) * rowsPerPage;
		return allActivities.slice(start, start + rowsPerPage);
	}, [allActivities, page, rowsPerPage]);

	// Helpers
	const getWorkPlanName = useCallback(
		(activity: PlanningActivity): string => {
			if (!activity.workPlan) return '';
			if (typeof activity.workPlan === 'number') {
				const workPlan = workPlans.find((e) => e.id === activity.workPlan);
				return workPlan?.name || '';
			}
			return activity.workPlan.name || '';
		},
		[workPlans]
	);

	const getEntityName = useCallback((activity: PlanningActivity): string => {
		if (!activity.entity) return '----';
		if (typeof activity.entity === 'number') return `Entity #${activity.entity}`;
		return activity.entity.name || '----';
	}, []);

	const getComponentName = useCallback((activity: PlanningActivity): string | null => {
		if (!activity.component) return null;
		if (typeof activity.component === 'number') return `${activity.component}. Component`;
		return activity.component.name || null;
	}, []);

	const getCategoryName = useCallback((activity: PlanningActivity): string | null => {
		if (!activity.category) return null;
		if (typeof activity.category === 'number') return `Category #${activity.category}`;
		return activity.category.name || null;
	}, []);

	const getExpenseTypeName = useCallback((activity: PlanningActivity): string | null => {
		if (!activity.expenseType) return null;
		if (typeof activity.expenseType === 'number') return `Expense #${activity.expenseType}`;
		return activity.expenseType.name || null;
	}, []);

	const formatTimeline = useCallback((dateStart: string | null, dateEnd: string | null): string => {
		if (!dateStart && !dateEnd) return t('noTimeline');
		if (dateStart && dateEnd) {
			return `${formatDate({ date: dateStart })} - ${formatDate({ date: dateEnd })}`;
		}
		return dateStart
			? `From ${formatDate({ date: dateStart })}`
			: dateEnd
				? `Until ${formatDate({ date: dateEnd })}`
				: '';
	}, []);

	// Navigation handlers
	const handleOpenActivity = useCallback(
		(activityId: number) => {
			navigate(`/project/${project?.idWbg}/spend-plans/activity/${activityId}`);
		},
		[navigate, project?.idWbg]
	);

	const handleOpenWorkPlan = useCallback(
		(workPlanId: number, event: React.MouseEvent) => {
			event.stopPropagation();
			navigate(`/project/${project?.idWbg}/spend-plans/work-planning/work-plan/${workPlanId}`);
		},
		[navigate, project?.idWbg]
	);

	// Column definitions - matching Figma exactly
	const columns: ColumnDef<PlanningActivity>[] = useMemo(
		() => [
			{
				id: 'name',
				label: t('activity'),
				minWidth: 280,
				sortable: true,
				render: (activity) => {
					const workPlanName = getWorkPlanName(activity);
					const workPlanId =
						typeof activity.workPlan === 'number' ? activity.workPlan : activity.workPlan?.id;

					return (
						<Stack direction="row" alignItems="flex-start" spacing={1.5}>
							<Avatar
								sx={{
									bgcolor: alpha(GRADIENTS.activity.icon, 0.12),
									color: GRADIENTS.activity.icon,
									height: 28,
									width: 28,
									flexShrink: 0,
									mt: 0.25,
								}}>
								<IconActivity sx={{ fontSize: 16 }} />
							</Avatar>
							<Box sx={{ minWidth: 0, maxWidth: 240 }}>
								<TruncatedText text={activity.name}>
									<ActivityLink onClick={() => handleOpenActivity(activity.id)}>
										{activity.name}
									</ActivityLink>
								</TruncatedText>
								{workPlanName && workPlanId && (
									<WorkPlanLink onClick={(e) => handleOpenWorkPlan(workPlanId, e)}>
										<TruncatedText text={workPlanName} variant="inherit" component="span" />
										<IconExternalLink sx={{ fontSize: 12, flexShrink: 0 }} />
									</WorkPlanLink>
								)}
							</Box>
						</Stack>
					);
				},
			},
			{
				id: 'entity',
				label: t('responsibleEntity'),
				minWidth: 140,
				sortable: true,
				sortKey: 'entity.name',
				render: (activity) => (
					<TruncatedText
						text={getEntityName(activity)}
						sx={{
							fontSize: 14,
							color: '#374151',
							maxWidth: 140,
						}}
					/>
				),
			},
			{
				id: 'component',
				label: t('component'),
				minWidth: 120,
				sortable: true,
				sortKey: 'component.name',
				render: (activity) => {
					const name = getComponentName(activity);
					return name ? (
						<TruncatedText text={name}>
							<ComponentText sx={{ maxWidth: 180 }}>{name}</ComponentText>
						</TruncatedText>
					) : (
						<Typography
							sx={{
								fontSize: 14,
								color: '#9ca3af',
							}}>
							—
						</Typography>
					);
				},
			},
			{
				id: 'category',
				label: t('category'),
				minWidth: 140,
				sortable: true,
				sortKey: 'category.name',
				render: (activity) => {
					const name = getCategoryName(activity);
					return name ? (
						<TruncatedText text={name}>
							<CategoryText sx={{ maxWidth: 180 }}>{name}</CategoryText>
						</TruncatedText>
					) : (
						<Typography
							sx={{
								fontSize: 14,
								color: '#9ca3af',
							}}>
							—
						</Typography>
					);
				},
			},
			{
				id: 'expenseType',
				label: t('expenseType'),
				minWidth: 120,
				sortable: true,
				sortKey: 'expenseType.name',
				render: (activity) => {
					const name = getExpenseTypeName(activity);
					return (
						<TruncatedText
							text={name || '—'}
							sx={{
								fontSize: 14,
								color: name ? '#0891b2' : '#9ca3af',
								maxWidth: 160,
							}}
						/>
					);
				},
			},
			{
				id: 'amount',
				label: t('plannedAmount'),
				minWidth: 120,
				align: 'right',
				sortable: true,
				render: (activity) => (
					<Box>
						<Typography
							sx={{
								fontSize: 14,
								fontWeight: 600, // Bold
								color: '#16a34a', // Green color like mockup
								fontFeatureSettings: '"tnum"', // Tabular numbers for alignment
							}}>
							{formatCurrency({
								currency: activity.currency || 'USD',
								value: activity.amount || 0,
							})}
						</Typography>
						{activity.amountLocal != null && activity.currencyLocal && (
							<Typography
								sx={{
									fontSize: 12.5,
									fontWeight: 500,
									color: '#6b7280',
									fontFeatureSettings: '"tnum"',
								}}>
								{formatCurrency({
									currency: activity.currencyLocal,
									value: activity.amountLocal,
								})}
							</Typography>
						)}
					</Box>
				),
			},
			{
				id: 'dateStart',
				label: t('timeline'),
				minWidth: 130,
				sortable: true,
				render: (activity) => (
					<Typography
						sx={{
							fontSize: 14,
							color: activity.dateStart || activity.dateEnd ? '#374151' : '#9ca3af',
						}}>
						{formatTimeline(activity.dateStart || null, activity.dateEnd || null)}
					</Typography>
				),
			},
			{
				id: 'status',
				label: t('progress'),
				minWidth: 90,
				sortable: true,
				sortKey: 'progress',
				render: (activity) => {
					const progress = activity.progress ?? 0;
					return <ProgressChip value={progress}>{progress}%</ProgressChip>;
				},
			},
		],
		[
			getWorkPlanName,
			getEntityName,
			getComponentName,
			getCategoryName,
			getExpenseTypeName,
			formatTimeline,
			handleOpenActivity,
			handleOpenWorkPlan,
		]
	);

	// Row actions
	const rowActions: RowAction<PlanningActivity>[] = useMemo(
		() => [
			{
				label: 'Edit Activity',
				onClick: onEditActivity,
				icon: <IconEdit sx={{ fontSize: 18, color: '#6b7280' }} />,
			},
			{
				label: 'Add Sub-Activity',
				onClick: onAddSubActivity,
				icon: <IconAdd sx={{ fontSize: 18, color: '#6b7280' }} />,
			},
			{
				label: 'Link Contract',
				onClick: onLinkContract,
				icon: <IconLink sx={{ fontSize: 18, color: '#6b7280' }} />,
			},
			{
				label: 'Delete Draft',
				onClick: (activity) => onDeleteDraftActivity?.(activity),
				icon: <IconDelete sx={{ fontSize: 18 }} />,
				// planning_activity uses PLANNED as its pre-activation / draft equivalent
				// (no DRAFT enum value exists for this resource).
				show: (activity) => activity.status === 'PLANNED' && Boolean(onDeleteDraftActivity),
				destructive: true,
				dividerBefore: true,
			},
		],
		[onEditActivity, onAddSubActivity, onLinkContract, onDeleteDraftActivity]
	);

	// Filter configurations for dropdown selects - matches old SearchFilterBar
	const filterConfigs: FilterConfig[] = useMemo(
		() => [
			{
				key: 'status',
				label: 'Status',
				options: [
					{ value: 'ALL', label: 'All Statuses' },
					{ value: 'CANCELLED', label: 'Cancelled' },
					{ value: 'COMPLETED', label: 'Completed' },
					{ value: 'DEACTIVATED', label: 'Deactivated' },
					{ value: 'DELAYED', label: 'Delayed' },
					{ value: 'IN_PROGRESS', label: 'In Progress' },
					{ value: 'PLANNED', label: 'Planned' },
				],
			},
			{
				key: 'workPlan',
				label: 'Work Plan',
				options: [
					{ value: 'ALL', label: 'All Work Plans' },
					...workPlans.map((wp) => ({ value: wp.id, label: wp.name })),
				],
			},
		],
		[workPlans]
	);

	return (
		<DataTable
			data={activities}
			columns={columns}
			getRowKey={(activity) => activity.id}
			loading={isLoading}
			emptyMessage={t('spendPlan.empty.activity.title')}
			emptyContent={t('spendPlan.empty.activity.content')}
			emptyActions={[
				{
					children: t('spendPlan.addActivity'),
					onClick: () => onAddActivity?.(),
					variant: 'contained',
				},
			]}
			orderBy={orderBy}
			order={order}
			onSortChange={onSortChange}
			page={page}
			rowsPerPage={rowsPerPage}
			totalCount={totalCount}
			onPageChange={onPageChange}
			onRowsPerPageChange={onRowsPerPageChange}
			searchPlaceholder={t('search')}
			searchValue={searchValue}
			onSearchChange={onSearchChange}
			rowActions={rowActions}
			onRowClick={(activity) => handleOpenActivity(activity.id)}
			showFilters
			filters={filterConfigs}
			filterValues={filterValues}
			onFilterChange={onFilterChange}
		/>
	);
};

export default ActivitiesDataTable;
